import { DotsThree } from '@phosphor-icons/react';
import { Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useCallback, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { COLOR } from '@styles/theme';

const LEAVE_DELAY_MS = 250;

export type HoverRowActionItem = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
};

type HoverRowActionsDropdownProps = {
  items: HoverRowActionItem[];
  onAction: (key: string) => void;
};

const actionButtonStyle = {
  background: COLOR.black,
  borderRadius: 5,
  width: 26,
  height: 26,
  minWidth: 26,
  padding: 0,
  flexShrink: 0,
} as const;

/**
 * Row "..." menu that opens on hover and keeps the overlay open while the
 * pointer moves to menu items so clicks register reliably.
 */
export function HoverRowActionsDropdown({
  items,
  onAction,
}: HoverRowActionsDropdownProps) {
  const [open, setOpen] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = undefined;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => setOpen(false), LEAVE_DELAY_MS);
  }, [clearLeaveTimer]);

  const openMenu = useCallback(() => {
    clearLeaveTimer();
    setOpen(true);
  }, [clearLeaveTimer]);

  const handleTriggerClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      clearLeaveTimer();
      setOpen(prev => !prev);
    },
    [clearLeaveTimer],
  );

  const menuItems: MenuProps['items'] = items.map(
    ({ key, label, icon, disabled, danger }) => ({
      key,
      label,
      icon,
      disabled,
      danger,
    }),
  );

  const handleMenuClick: MenuProps['onClick'] = useCallback(
    ({ key, domEvent }) => {
      domEvent.stopPropagation();
      onAction(String(key));
      setOpen(false);
    },
    [onAction],
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={next => {
        clearLeaveTimer();
        setOpen(next);
      }}
      menu={{ items: menuItems, onClick: handleMenuClick }}
      placement="bottomRight"
      dropdownRender={menu => (
        <div onMouseEnter={clearLeaveTimer} onMouseLeave={scheduleClose}>
          {menu}
        </div>
      )}
    >
      <span
        className="inventory-row-actions-trigger"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        <Button
          type="text"
          className="inventory-row-actions"
          icon={<DotsThree size={20} weight="bold" color={COLOR.white} />}
          onClick={handleTriggerClick}
          onMouseDown={e => e.stopPropagation()}
          style={actionButtonStyle}
        />
      </span>
    </Dropdown>
  );
}
